import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { useAuth, API } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Briefcase, Eye, MousePointerClick, Users, TrendingUp } from "lucide-react";
import StatusBadge from "./StatusBadge";

const RANGES = [
  { id: 30, labelEn: "30D", labelFr: "30J" },
  { id: 90, labelEn: "3M", labelFr: "3M" },
  { id: 180, labelEn: "6M", labelFr: "6M" },
  { id: 365, labelEn: "1Y", labelFr: "1A" },
];

export default function EmployerReport({ employerId, open, onOpenChange }) {
  const { authHeader } = useAuth();
  const { lang } = useI18n();
  const [report, setReport] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!employerId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/employers/${employerId}/report`, { headers: authHeader, params: { days } });
      setReport(r.data);
    } finally { setLoading(false); }
  }, [employerId, days, authHeader]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="employer-report-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {lang === "fr" ? "Rapport employeur" : "Employer report"} — {report?.employer?.company_name || "…"}
          </DialogTitle>
        </DialogHeader>

        {/* Range */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{lang === "fr" ? "Période" : "Range"}:</span>
          <div className="inline-flex items-center bg-slate-100 rounded-full p-1 gap-1">
            {RANGES.map(r => (
              <button
                key={r.id}
                data-testid={`employer-report-range-${r.id}`}
                onClick={() => setDays(r.id)}
                className={`text-xs px-3 py-1 rounded-full ${days === r.id ? "bg-[#1A8FE3] text-white" : "text-slate-700 hover:bg-white"}`}
              >
                {lang === "fr" ? r.labelFr : r.labelEn}
              </button>
            ))}
          </div>
          {loading && <span className="text-xs text-slate-400">{lang === "fr" ? "Chargement…" : "Loading…"}</span>}
        </div>

        {report && (
          <>
            {/* Header info */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
              <Info label="Email" value={report.employer.email} />
              <Info label="Plan" value={report.employer.plan || "starter"} />
              <Info label={lang === "fr" ? "Crédits" : "Credits"} value={report.employer.credits} />
              <Info label="ID" value={report.employer.id?.slice(0, 8)} mono />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <KPI icon={Briefcase} label={lang === "fr" ? "Offres totales" : "Total jobs"} value={report.totals.jobs} />
              <KPI icon={Briefcase} label={lang === "fr" ? "Offres actives" : "Active jobs"} value={report.totals.active_jobs} />
              <KPI icon={Users} label={lang === "fr" ? "Candidatures" : "Applications"} value={report.totals.applications} />
              <KPI icon={Eye} label={lang === "fr" ? "Vues" : "Page views"} value={report.totals.page_views} />
              <KPI icon={MousePointerClick} label={lang === "fr" ? "Clics postuler" : "Apply clicks"} value={report.totals.apply_clicks} />
            </div>

            {/* Daily applications chart */}
            <Card className="p-4">
              <h4 className="font-display text-sm font-semibold inline-flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-[#1A8FE3]" />
                {lang === "fr" ? "Candidatures par jour" : "Applications per day"}
              </h4>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={report.daily_applications}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 10 }} interval={"preserveStartEnd"} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                    <Line type="monotone" dataKey="applications" stroke="#1A8FE3" strokeWidth={2.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Status breakdown */}
            {report.status_breakdown?.length > 0 && (
              <Card className="p-4">
                <h4 className="font-display text-sm font-semibold mb-3">
                  {lang === "fr" ? "Pipeline candidatures" : "Application pipeline"}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {report.status_breakdown.map(s => (
                    <div key={s.status} className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-3 py-1 text-xs">
                      <StatusBadge status={s.status} />
                      <span className="font-mono text-slate-700">{s.count}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Jobs list */}
            <Card className="overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 font-semibold text-sm">
                {lang === "fr" ? "Toutes les offres (actives & historiques)" : "All jobs (active & historical)"}
              </div>
              <div className="overflow-x-auto max-h-72">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-700 uppercase text-[10px] tracking-wider sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">{lang === "fr" ? "Titre" : "Title"}</th>
                      <th className="px-3 py-2 text-left">{lang === "fr" ? "Lieu" : "Location"}</th>
                      <th className="px-3 py-2 text-left">{lang === "fr" ? "Statut" : "Status"}</th>
                      <th className="px-3 py-2 text-right">{lang === "fr" ? "Vues" : "Views"}</th>
                      <th className="px-3 py-2 text-right">{lang === "fr" ? "Candidatures" : "Apps"}</th>
                      <th className="px-3 py-2 text-left">{lang === "fr" ? "Créé" : "Created"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.jobs.map(j => (
                      <tr key={j.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-medium">{j.title}</td>
                        <td className="px-3 py-2 text-slate-600">{j.location}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${j.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}>{j.status}</span>
                        </td>
                        <td className="px-3 py-2 text-right">{j.views || 0}</td>
                        <td className="px-3 py-2 text-right">{j.applications_count || 0}</td>
                        <td className="px-3 py-2 text-slate-600">{j.created_at ? new Date(j.created_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                    {report.jobs.length === 0 && (
                      <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 text-xs">{lang === "fr" ? "Aucune offre" : "No jobs"}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KPI({ icon: Icon, label, value }) {
  return (
    <Card className="p-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">{label}</span>
        <Icon className="h-4 w-4 text-[#1A8FE3]" />
      </div>
      <div className="font-display text-xl font-bold text-slate-900 mt-1">{value}</div>
    </Card>
  );
}

function Info({ label, value, mono }) {
  return (
    <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 text-slate-900 ${mono ? "font-mono text-xs" : "font-medium text-sm"}`}>{value || "—"}</div>
    </div>
  );
}
